import { useEffect, useState } from "react";
import { LdButton, LdIcon } from "@emdgroup-liquid/liquid/dist/react";
import { StyledButtonTab } from "@src/components/ButtonTab/ButtonTab.style";
import { StyledColumn, StyledRow } from "@src/styles";
import { StyledSpanRed, StyledText } from "./PotentialOfftarget.style";
import { OfftargetSummary, POfftargetResponse } from "@src/types";
import { useNavigate } from "react-router-dom";
import { ResultTable } from "@src/components";
import { ShowTranscriptMesage } from "../../HomePage";
import { CRIPSER_PATH, excelTopLink, USER_ID } from "@src/config";
import useIframeHeight from "@src/components/IframeHeight/Iframe";
import { useSelector } from "react-redux";
import { RootState } from "@src/features/store/store";
import { StyledDownload } from "../ResultView/ResultView.style";
import {
  FormValuesAmplicon,
  ViewPrimerDesignModal,
} from "../ResultView/ResultView";
import { generateUUID, timestampForFileName } from "@src/utils/utils";
import { useGetPrimerDesignDownload } from "@src/query/editor/EditorQuery";
import {
  PRIMER_DOWNLOAD_HEADER_OFFTARGET,
  PRIMER_DOWNLOAD_KEYS,
} from "@src/constants";
import * as XLSX from "xlsx";

function PotentialOfftarget() {
  const [summmaryData, setSummaryData] = useState<OfftargetSummary[] | null>(
    null
  );
  const [detailsData, setDetailsData] = useState<POfftargetResponse | null>(
    null
  );
  const navigate = useNavigate();
  // get offtarget data
  const offtargetData = useSelector(
    (state: RootState) => state.data.offtargetData
  );
  useEffect(() => {
    if (offtargetData) {
      setDetailsData(offtargetData.details);
      setSummaryData(offtargetData.summary);
    }
  }, [offtargetData]);

  //Adding logic for height
  useIframeHeight(CRIPSER_PATH);

  // Logic to highlight letter based on mismatch
  const highlightLetterInRed = (text: string, row: any, rowIndex?: number) => {
    const result = text.split("").map((letter: string, index: number) =>
      row.target_sequence[index] === letter ? (
        letter
      ) : (
        <StyledSpanRed
          potentialOfftarget={true}
          key={`red-hightlight-${rowIndex}-${index}`}
        >
          {letter}
        </StyledSpanRed>
      )
    );
    return <>{result}</>;
  };

  // PRIMER DESIGN
  const [showPrimerDesignModal, setPrimerDesignModal] =
    useState<boolean>(false);
  const [selectedResultItem, setSelectedResultItem] = useState<any | null>(
    null
  );

  const [loadingPrimerDownload, setLoadingPrimerDownload] =
    useState<boolean>(false);
  const { mutateAsync: getPrimerDownload } = useGetPrimerDesignDownload(
    setLoadingPrimerDownload
  );

  // Show primer design modal

  const onPrimerDesignModalClick = (selectedItem: any) => {
    if (selectedItem) {
      setSelectedResultItem(selectedItem);
    }
    setPrimerDesignModal(true);
  };

  const onPrimerDesignModalClose = () => {
    setPrimerDesignModal(false);
    setSelectedResultItem(null);
  };

  // Call Download primer API
  const onDownloadPrimer = async (payload: FormValuesAmplicon) => {
    const mode = sessionStorage.getItem("mode");
    const analysis_random_id =
      mode === "codon"
        ? offtargetData?.summary[0].stop_codon_analysis_random_id
        : offtargetData?.summary[0].base_editor_analysis_random_id;
    const source_analysis =
      mode === "codon"
        ? "stop_codon_design_analysis"
        : "dna_base_editor_analysis";
    setLoadingPrimerDownload(true);
    const { data } = await getPrimerDownload({
      user_id: USER_ID || "user_1",
      project_id: generateUUID(),
      analysis_module: "primer_design",
      source_analysis,
      source_analysis_random_id: analysis_random_id ?? "",
      grna: selectedResultItem.target_sequence,
      grna_id: selectedResultItem.gRNA_id,
      table_source: "off_target",
      chromosome: selectedResultItem.chromsome, // null if on_target
      start_coordinate: selectedResultItem.start_coordinate, // null if on_target
      genome: offtargetData?.genome ?? "",
      amplicon_min: payload.amplicon_min,
      amplicon_opt: payload.amplicon_opt,
      amplicon_max: payload.amplicon_max,
    });

    const arrGRNA = selectedResultItem.potential_off_target_site.split(",");
    const arrGRNA_Id = selectedResultItem.gRNA_id.split(",");
    const primerData = data?.data.map((primer: any) => {
      const gRNAIndex = arrGRNA_Id.findIndex(
        (ele: any) => ele === primer.primer_name
      );
      if (gRNAIndex !== -1) {
        return { ...primer, targetSequence: arrGRNA[gRNAIndex] };
      }
      return { ...primer };
    });

    downloadExcel(primerData);
    setPrimerDesignModal(false);
  };

  // download excel

  const downloadExcel = (data: any) => {
    const processData = (pData: []) =>
      pData.map((item: any) =>
        PRIMER_DOWNLOAD_KEYS.flatMap((key: string) => item[key])
      );
    const workBook = XLSX.utils.book_new();
    const worksheetData = processData(data);

    // Create a hyperlink cell for the very top row (A1)
    const hyperlinkText = excelTopLink.text;
    const hyperlinkUrl = excelTopLink.link_url;
    const topLinkRow: any[] = [
      {
        t: "s",
        v: hyperlinkText,
        l: { Target: hyperlinkUrl, Tooltip: "Click to open" },
      },
    ];

    const workSheet = XLSX.utils.aoa_to_sheet([
      topLinkRow,
      PRIMER_DOWNLOAD_HEADER_OFFTARGET,
      ...worksheetData,
    ]);
    XLSX.utils.book_append_sheet(workBook, workSheet, "sheet1");
    XLSX.writeFile(workBook, `primer_design_${timestampForFileName()}.xlsx`);
  };
  // PRIMER DOWNLOAD ENDS

  const tableDefSummary = {
    tableTitle: "Potential Off-target Summary",
    uniqueKey: "gRNA_id",
    columns: [
      {
        name: "gRNA ID",
        dataKey: "gRNA_id",
      },
      { name: "Target Sequence", dataKey: "gRNA" },
      {
        name: "No. of mismatches",
        key: "noofmismatches",
        children: [
          {
            name: "0",
            dataKey: "offtargetZero",
            key: "offtargetZero",
          },
          {
            name: "1",
            dataKey: "offtargetOne",
            key: "offtargetOne",
          },
          {
            name: "2",
            dataKey: "offtargetTwo",
            key: "offtargetTwo",
          },
          {
            name: "3",
            dataKey: "offtargetThree",
            key: "offtargetThree",
          },
        ],
      },
    ],
  };

  const tableDefDetails = {
    tableTitle: "Potential Off-target Details",
    uniqueKey: "unique_id",
    columns: [
      {
        name: "gRNA ID",
        sortable: false,
        dataKey: "gRNA_id",
      },
      { name: "Target Sequence", sortable: true, dataKey: "target_sequence" },
      {
        name: "Chromsome",
        sortable: true,
        dataKey: "chromSort",
        render: (text: string, row: any) => row.chromsome,
      },
      { name: "Strand", sortable: true, dataKey: "strand" },
      { name: "Start Coordinate", sortable: true, dataKey: "start_coordinate" },
      {
        name: "Potential off-target sites",
        dataKey: "potential_off_target_site",
        key: "potential_off_target_site",
        sortable: true,
        render: (text: string, row: any, index?: number) =>
          highlightLetterInRed(text, row, index),
      },
      {
        name: "Position of Mismatch",
        dataKey: "position_of_mismatch",
        key: "position_of_mismatch",
        sortable: true,
      },
      {
        name: "Mismatch Count",
        dataKey: "mismatch_count",
        key: "mismatch_count",
        sortable: true,
      },
      {
        name: "Design Primer(s)",
        render: (text: string, row: any) => {
          return (
            <StyledDownload
              mode="neutral-ghost"
              onClick={() => onPrimerDesignModalClick(row)}
            >
              <LdIcon>
                <svg
                  width="800px"
                  height="800px"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 3a1 1 0 0 1 1 1v9.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 13.586V4a1 1 0 0 1 1-1Z"
                    fill="#000000"
                  />
                  <path
                    d="M6 17a1 1 0 1 0-2 0v.6C4 19.482 5.518 21 7.4 21h9.2c1.882 0 3.4-1.518 3.4-3.4V17a1 1 0 1 0-2 0v.6c0 .778-.622 1.4-1.4 1.4H7.4c-.778 0-1.4-.622-1.4-1.4V17Z"
                    fill="#000000"
                  />
                </svg>
              </LdIcon>
            </StyledDownload>
          );
        },
      },
    ],
  };

  return (
    <StyledRow direction="column">
      <StyledColumn width="100">
        <StyledButtonTab>
          <LdButton onClick={() => navigate(-1)}>Back</LdButton>
          <StyledText variant="h5">Potential Offtargets</StyledText>
        </StyledButtonTab>
        <StyledRow direction="column">
          <StyledColumn width="100">
            {summmaryData && tableDefSummary ? (
              <ResultTable
                data={summmaryData}
                tableDef={tableDefSummary}
                summaryData={[]}
                pagination={false}
              />
            ) : null}
          </StyledColumn>
        </StyledRow>
        <StyledRow direction="column">
          <StyledColumn width="100">
            {detailsData?.data.off_targets_results && detailsData.data.off_targets_results.length > 0 && tableDefDetails && (
              <ResultTable
                data={detailsData.data.off_targets_results}
                tableDef={tableDefDetails}
                offTargetDownload={true}
                summaryData={summmaryData}
              />
            )}

            {/* {detailsData?.data.off_targets_results && tableDefDetails ? (
              <ResultTable
                data={detailsData?.data.off_targets_results}
                tableDef={tableDefDetails}
                offTargetDownload={true}
                summaryData={summmaryData}
              />
            ) : null} */}
            {detailsData?.error_message?.length ? (
              <StyledRow>
                <StyledColumn width="100">
                  <ShowTranscriptMesage
                    msg={detailsData?.error_message}
                    headLine="Error"
                    mode="error"
                  />
                </StyledColumn>
              </StyledRow>
            ) : null}
          </StyledColumn>
        </StyledRow>
        <ViewPrimerDesignModal
          onModalClose={onPrimerDesignModalClose}
          showModal={showPrimerDesignModal}
          onDownloadPrimer={onDownloadPrimer}
          loading={loadingPrimerDownload}
        />
      </StyledColumn>
    </StyledRow>
  );
}

export { PotentialOfftarget };
